import { initializeApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth'
import { getDatabase } from 'firebase/database'

// Every value comes from a VITE_FIREBASE_* env var — nothing secret is committed.
// These are build-time PUBLIC values: Vite inlines them into the client bundle,
// so anyone can read them off the deployed page. That is fine — the Firebase web
// config is not a credential, and database.rules.json is what protects the data.
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // Must be the SAME ORIGIN the app is served from (gwfinder.web.app), not the
  // default gwfinder.firebaseapp.com. Firebase Hosting serves the auth handler
  // at /__/auth/handler on both domains, and keeping it same-origin is what
  // makes signInWithRedirect survive Safari's storage partitioning inside an
  // installed iOS PWA. Cross-origin, the redirect loses its state and the
  // sign-in silently never completes.
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_RTDB_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

export const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY

/** False until the Firebase project is wired up. The app renders setup
 *  instructions instead of crashing, so dev and build work without config. */
export const isConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.databaseURL)

export const app = isConfigured ? initializeApp(firebaseConfig) : null
export const auth = app ? getAuth(app) : null
export const db = app ? getDatabase(app) : null

const provider = new GoogleAuthProvider()
// Always show the account chooser. Without this, a browser signed into several
// Google accounts silently picks one, and picking the wrong one looks identical
// to being unauthorized.
provider.setCustomParameters({ prompt: 'select_account' })

/**
 * Redirect rather than popup, everywhere.
 *
 * signInWithPopup cannot work in an iOS standalone PWA — there is no window to
 * open, and the opener relationship needed to return the credential does not
 * survive. Redirect works in both contexts, so there is one code path rather
 * than a branch that is only exercised on one device.
 */
export function startSignIn() {
  if (!auth) return Promise.reject(new Error('Firebase is not configured'))
  return signInWithRedirect(auth, provider)
}

export function endSession() {
  return auth ? signOut(auth) : Promise.resolve()
}

/**
 * Completes a redirect sign-in if we just came back from one. Returns the error
 * if it failed, so the UI can say why instead of bouncing back to a sign-in
 * button with no explanation.
 */
export async function completeRedirect() {
  if (!auth) return null
  try {
    await getRedirectResult(auth)
    return null
  } catch (err) {
    return err
  }
}

/** Subscribe to auth state. Returns the unsubscribe function. */
export function watchAuth(onUser, onError) {
  if (!auth) return () => {}
  return onAuthStateChanged(auth, onUser, onError)
}
